import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';
import { formatDate, formatTime } from '../utils/dateUtils';

export const useAdminMessages = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_read: '',
    message_type: '',
    date_from: '',
    date_to: '',
    ...initialFilters
  });

  const readStatusOptions = [
    { value: '', label: 'All Messages' },
    { value: 'true', label: 'Read' },
    { value: 'false', label: 'Unread' }
  ];

  const messageTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'text', label: 'Text' },
    { value: 'system', label: 'System' },
    { value: 'booking', label: 'Booking' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: messagesResponse, isLoading, error } = useQuery({
    queryKey: ['adminMessages', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/messages${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/messages/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminMessages']);
    }
  });

  const messages = useMemo(() => {
    return messagesResponse?.data?.data || messagesResponse?.data || [];
  }, [messagesResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(message => 
      message.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.sender?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.sender?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.receiver?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.receiver?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.booking?.booking_reference?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const statistics = useMemo(() => {
    const totalMessages = messages.length;
    const unreadMessages = messages.filter(m => !m.is_read).length;
    const readMessages = messages.filter(m => m.is_read).length;

    return {
      totalMessages,
      unreadMessages,
      readMessages
    };
  }, [messages]);

  return {
    messages,
    isLoading,
    error,
    filters,
    readStatusOptions,
    messageTypeOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    deleteMessage: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending
  };
};

